package dev.lingvo.site.pages.app

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.InsightsDto
import dev.lingvo.shared.LocaleInsightDto
import dev.lingvo.shared.localeFlagPrefix
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.ds.ChartPalette
import dev.lingvo.site.ds.ChartPoint
import dev.lingvo.site.ds.ChartSeries
import dev.lingvo.site.ds.ChartState
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LChartLineIcon
import dev.lingvo.site.ds.LClockIcon
import dev.lingvo.site.ds.LCoinsIcon
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LLanguagesIcon
import dev.lingvo.site.ds.LLineChart
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LSegmentBar
import dev.lingvo.site.ds.LSegmented
import dev.lingvo.site.ds.LSegmentedOption
import dev.lingvo.site.ds.LStatDelta
import dev.lingvo.site.ds.LStatTile
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.WorkspaceSelection
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * How the work is going, what it costs, and when each language will be finished.
 *
 * **The dashboard could say what fraction of a language was done and nothing else.** Not whether it
 * was accelerating, not what it cost, and — the omission that matters most —
 * not when it would be finished. A percentage tells a release manager they are at 62% and nothing
 * about whether that is three days or three months away, which is the only form of the answer that
 * can go in a release plan.
 *
 * Everything here comes from what the product already recorded: a history row per save, a credit
 * event per reservation. Nothing new is written to draw it.
 */
@Page("/app/insights")
@Layout(".components.layouts.AppLayout")
@Composable
fun InsightsPage() {
    val ctx = rememberPageContext()
    var days by remember { mutableStateOf(30) }
    // A key the retry button can actually change. `days = days` writes the same value, so the
    // state never changed, `loadAsync` never re-ran, and Retry was a button that did nothing.
    var attempt by remember { mutableStateOf(0) }
    val workspace = WorkspaceSelection.current()
    val slug = workspace?.slug
    val state = loadAsync(slug, days, attempt) { slug?.let { ApiClient.insights(it, days) } }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        // **The range control belongs to the header, not beside it.** It used to sit in a wrapping
        // `Row` next to `PageHeader`, which meant two components competing for the same line and a
        // `Spacer` holding them apart by hand; `PageHeader.actions` is the slot for exactly this,
        // and it wraps on its own. This is the one control the page exists to be driven by, so it
        // is also the page's single primary action — nothing else here takes one.
        PageHeader(
            t("app.insights.insights", "Insights"),
            t("app.insights.how_much_is_moving_what_it", "How much is moving, what it costs, and when each language lands."),
        ) {
            LSegmented(
                options = listOf(
                    LSegmentedOption(7, t("app.insights.7_days", "7 days")),
                    LSegmentedOption(30, t("app.insights.30_days", "30 days")),
                    LSegmentedOption(90, t("app.insights.90_days", "90 days")),
                ),
                selected = days,
                onSelected = { days = it },
                label = t("app.insights.time_range", "Time range"),
            )
        }

        LAsyncContent(
            state,
            rows = 3,
            loadingLabel = t("app.insights.loading_insights", "Loading insights"),
            // The endpoint requires manage access, so a translator following a shared link meets a
            // refusal here — and read "You don't have access to this page" because no subject was
            // ever passed.
            subject = t("app.insights.insights_subject", "insights"),
            onRetry = { attempt++ },
        ) { insights ->
            if (insights == null) {
                // `slug` is null only for somebody in no workspace at all. `loadAsync` wraps that
                // as a successful null, and the page drew a title, a live range control, and then
                // nothing whatsoever.
                LEmptyState(
                    t("app.insights.no_workspace_selected", "No workspace selected"),
                    t("app.insights.insights_count_what_the_projects_in", "Insights count what the projects in a workspace have moved. Create one, " +
                        "or ask whoever invited you to send the invitation again."),
                    Modifier.fillMaxWidth(),
                    LEmptyStateIcon.INSIGHTS,
                ) {
                    LButton(
                        t("app.insights.create_a_workspace", "Create a workspace"),
                        { ctx.router.tryRoutingTo("/app/workspaces") },
                    )
                }
            } else {
                // The stat strip and the cards below it are siblings, and the space between them is
                // this page's own rhythm — it comes from here rather than from the slot around it.
                Column(Modifier.fillMaxWidth().gap(Space.XL)) {
                    Body(insights) { path -> ctx.router.tryRoutingTo(path) }
                }
            }
        }
    }
}

@Composable
private fun Body(insights: InsightsDto, onNavigate: (String) -> Unit) {
    val colorMode = ColorMode.current
    if (insights.stringsTranslated == 0 && insights.locales.isEmpty()) {
        LEmptyState(
            t("app.insights.nothing_has_moved_yet", "Nothing has moved yet"),
            t("app.insights.once_strings_are_imported_and_somebody", "Once strings are imported and somebody starts translating, this page shows " +
                "how fast the work is going, what machine translation is costing, and when each " +
                "language will be finished."),
            Modifier.fillMaxWidth(),
            LEmptyStateIcon.INSIGHTS,
        ) {
            // The slot was empty, so the page that says "nothing has moved yet" offered nowhere to
            // go and make something move.
            LButton(
                t("app.insights.see_all_projects", "See all projects"),
                { onNavigate("/app/projects") },
                variant = LButtonVariant.SECONDARY,
            )
        }
        return
    }

    Row(Modifier.fillMaxWidth().gap(Space.MD).styleModifier { property("flex-wrap", "wrap") }) {
        Stat(
            t("app.insights.strings_translated", "Strings translated"),
            insights.stringsTranslated,
            LStatDelta(
                tPlural(
                    "app.insights.count.window_day",
                    insights.days,
                    "in the last {count} day",
                    "in the last {count} days",
                ),
            ),
        ) { LChartLineIcon() }
        Stat(
            t("app.insights.credits_spent", "Credits spent"),
            insights.creditsSpent,
            LStatDelta(t("app.insights.machine_translation", "machine translation")),
        ) { LCoinsIcon() }
        Stat(
            t("app.insights.languages", "Languages"),
            insights.locales.size,
            LStatDelta(t("app.insights.with_strings_in_them", "with strings in them")),
        ) { LLanguagesIcon() }
        // **Which language, not just how many days.** The page exists to say when each language
        // lands, and its headline tile named a number with nothing attached to it.
        insights.locales
            .mapNotNull { locale -> locale.daysToFinish?.takeIf { it > 0 }?.let { locale to it } }
            .minByOrNull { (_, left) -> left }
            ?.let { (locale, soonest) ->
                Stat(
                    // The separator belongs to the sentence, so a translator can move it.
                    tf(
                        "app.insights.next_to_finish_language",
                        "Next to finish · {language}",
                        "language" to localeInfo(locale.locale).englishName,
                    ),
                    soonest,
                    LStatDelta(t("app.insights.days_at_the_current_rate", "days at the current rate")),
                ) { LClockIcon() }
            }
    }

    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            SectionTitle(
                t("app.insights.throughput", "Throughput"),
                t("app.insights.saves_per_day_by_where_the", "Saves per day, by where the text came from."),
            ) { LChartLineIcon() }
            // Origin rather than a bare total: a line that is all machine is spending credits, and
            // one that is all human is spending salary. Nothing showed which.
            LLineChart(
                series = insights.throughput.map { s ->
                    ChartSeries(s.name, s.points.map { ChartPoint(it.label, it.value) })
                },
                area = true,
                emptyMessage = t("app.insights.no_translations_were_saved_in_this", "No translations were saved in this period."),
            )
        }
    }

    if (insights.spend.isNotEmpty()) {
        LCard(Modifier.fillMaxWidth()) {
            Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                SectionTitle(
                    t("app.insights.machine_translation_spend", "Machine translation spend"),
                    t("app.insights.credits_per_day_by_provider_net", "Credits per day, by provider, net of refunds."),
                ) { LCoinsIcon() }
                LLineChart(
                    series = insights.spend.map { s ->
                        ChartSeries(s.name, s.points.map { ChartPoint(it.label, it.value) })
                    },
                    emptyMessage = t("app.insights.no_credits_were_spent_in_this", "No credits were spent in this period."),
                )
            }
        }
    }

    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            SectionTitle(
                t("app.insights.languages", "Languages"),
                tf(
                    "app.insights.how_far_along_each_one_is",
                    "How far along each one is, and when it lands if the last {days} days are " +
                    "typical.", "days" to insights.days.toString()),
            ) { LLanguagesIcon() }
            insights.locales.forEach { locale -> LocaleRow(locale, colorMode) }
        }
    }
}

@Composable
private fun LocaleRow(locale: LocaleInsightDto, colorMode: ColorMode) {
    val palette = colorMode.toSitePalette()
    val info = localeInfo(locale.locale)
    val untranslated = (locale.total - locale.translated).coerceAtLeast(0)
    val awaiting = (locale.translated - locale.approved).coerceAtLeast(0)

    Column(Modifier.fillMaxWidth().gap(Space.XS)) {
        Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
            Span(Modifier.fontWeight(600).fontSize(TypeScale.Small).toAttrs()) {
                Text("${localeFlagPrefix(info.code)}${info.englishName}")  // i18n-ignore — a flag glyph immediately followed by a language name — no separator, no words, nothing a translator could change
            }
            Spacer()
            // The forecast, stated plainly. This is the number the whole page exists for — and it
            // was four hardcoded English branches on the page that sells real plurals.
            Span(Modifier.fontSize(TypeScale.Small).color(palette.textSecondary).toAttrs()) {
                val left = locale.daysToFinish
                Text(
                    when {
                        left == 0 -> t("app.insights.complete", "Complete")
                        left == null -> t("app.insights.no_recent_activity", "No recent activity")
                        else -> tPlural(
                            "app.insights.count.day_left",
                            left,
                            "About {count} day left",
                            "About {count} days left",
                        )
                    },
                )
            }
        }
        LSegmentBar(
            segments = listOf(
                Triple(t("app.insights.approved", "Approved"), locale.approved.toDouble(), ChartPalette.forState(colorMode, ChartState.APPROVED)),
                Triple(t("app.insights.awaiting_review", "Awaiting review"), awaiting.toDouble(), ChartPalette.forState(colorMode, ChartState.TRANSLATED)),
                // **Untranslated is empty track, not a third grey.** Painted as a chart state it
                // measured 1.42:1 against "awaiting review" in light and 1.57:1 in dark — under the
                // 3:1 non-text floor, so the two segments read as one block and German looked 62%
                // done rather than 53% — and the pair inverted between modes, so the same bar told
                // two opposite stories. `surfaceSunken` is the bar's own ground, which makes the
                // distinction fill-versus-empty exactly as the 20-cell bar on /app/projects does.
                // The count itself is still in the row below and in the chart's table.
                Triple(t("app.insights.untranslated", "Untranslated"), untranslated.toDouble(), palette.surfaceSunken),
            ),
            // The bar's whole accessible name, and it was an English template — the one thing on
            // this page a screen reader reads per language.
            ariaLabel = trf(
                "app.insights.language_progress",
                "{language} progress",
                "language" to info.englishName,
            ),
        )
        Span(Modifier.fontSize(TypeScale.Micro).color(palette.textSecondary).toAttrs()) {
            Text(
                tf(
                    "app.insights.approved_awaiting_untranslated_counts",
                    "{approved} approved · {awaiting} awaiting review · {untranslated} untranslated",
                    "approved" to formatCount(locale.approved),
                    "awaiting" to formatCount(awaiting),
                    "untranslated" to formatCount(untranslated),
                ),
            )
        }
    }
}

/**
 * A headline figure. Deliberately still.
 *
 * These four used to assemble with `enterStaggered`, which is right for a list a page draws once.
 * This page is not drawn once: the range control above is "the one control this page exists to be
 * driven by", `days` is a `loadAsync` key, and `loadAsync` resets to `Async.Loading` on every key
 * change — so each press of 7/30/90 unmounts the whole success subtree and remounts it. The tiles
 * were therefore cascading back into place, up to 360ms of movement, every time somebody compared
 * two ranges. Comparing numbers is the entire job of this control, and numbers that slide while you
 * are reading them are the one thing that makes it harder.
 */
@Composable
private fun Stat(label: String, value: Int, note: LStatDelta, icon: @Composable () -> Unit) {
    LStatTile(
        label = label,
        value = formatCount(value),
        modifier = Modifier.styleModifier { property("flex", "1"); property("min-width", "180px") },
        icon = icon,
        // The scope line, in the slot the design system keeps for a figure's context. It carries
        // no direction because none of these four is a trend — `LStatDirection.FLAT` renders the
        // sentence and no arrow.
        delta = note,
    )
}

/**
 * `H2`, not a bold `Span` — and the design system's `H2`, not this page's own.
 *
 * These three titles are the only section structure the page has, and as spans the whole of
 * Insights had exactly one heading — its `H1` — so a screen reader offered no way to move between
 * "Throughput", "Machine translation spend" and "Languages" at all. [LSectionTitle] owns that role
 * everywhere else in the product; this file had written its own `H2` beside it.
 */
@Composable
private fun SectionTitle(title: String, subtitle: String, icon: @Composable () -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    Column(Modifier.gap(Space.XXS)) {
        LSectionTitle(title, Modifier.fontSize(TypeScale.BodySmall), icon = icon)
        Span(Modifier.fontSize(TypeScale.Small).color(palette.textSecondary).toAttrs()) { Text(subtitle) }
    }
}
